import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import Block from "@/components/Block";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useCheckout, useDraftBooking } from "@/hooks";
import { analytics } from "@/utils/analytics";
import { computeAmountToPayOnline, formatCurrency } from "@/utils/pricing";
import BookingStepLayout from "./components/BookingStepLayout";
import DraftBookingDialog from "./components/DraftBookingDialog";

export default function Payment() {
  const { state } = useBookingContext();
  const { i18n, t } = useLingui();
  const price = state.price;
  const amountToPayOnline = price ? computeAmountToPayOnline(price) : 0;
  const formatPrice = (value: number) =>
    price ? formatCurrency(value, price.currency, i18n.locale) : null;
  const [consent, setConsent] = useState({
    termsAccepted: false,
    privacyAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const { submitCheckout } = useCheckout();
  const { isDraftBookingEnabled, isSaving, draftBookingLink, saveDraft, clearDraftLink } =
    useDraftBooking();

  const canSaveDraft = isDraftBookingEnabled && !!state.billing.mail;
  const submitDisabled = !consent.termsAccepted || !consent.privacyAccepted;

  if (!price) {
    return null;
  }

  const onSubmit = async () => {
    analytics.capture("checkout_submitted", {
      payment_system: price.payment_system,
      price_total: price.total,
      currency: price.currency,
    });
    setLoading(true);
    const result = await submitCheckout();
    setLoading(false);
    const defaultErrorMessage = i18n._(msg`Failed to create booking`);

    if (result instanceof Error) {
      analytics.capture("checkout_failed", {
        payment_system: price.payment_system,
        price_total: price.total,
        currency: price.currency,
      });
      enqueueSnackbar(result.message || defaultErrorMessage, {
        variant: "error",
      });
      return false;
    }
    return true;
  };

  let paymentInfo = null;
  let nextButtonText = null;

  switch (price.payment_system) {
    case "ONLINE":
      paymentInfo = (
        <>
          <Typography>
            <Trans>Secure your booking with an online payment.</Trans>
          </Typography>
          <Typography>
            <Trans>
              The total cost of your booking is <b>{formatPrice(price?.total)}</b>
            </Trans>
          </Typography>
          <Typography>
            <Trans>
              You have to pay now <b>{formatPrice(amountToPayOnline)}</b> as a deposit for your
              booking.
            </Trans>
          </Typography>
        </>
      );
      nextButtonText = t`Confirm & pay`;
      break;

    case "ON_SITE":
      paymentInfo = (
        <>
          <Typography>
            <Trans>Payment is due at the campsite upon arrival.</Trans>
          </Typography>
          <Typography>
            <Trans>
              The total cost of your booking is <b>{formatPrice(price?.total)}</b>
            </Trans>
          </Typography>
        </>
      );
      nextButtonText = t`Confirm booking`;
      break;

    case "INVOICE":
      paymentInfo = (
        <>
          <Typography>
            <Trans>
              After completing your booking, you will receive a message with the payment
              information. Please make the payment within the specified deadline to confirm your
              booking.
            </Trans>
          </Typography>
          <Typography>
            <Trans>
              The total cost of your booking is <b>{formatPrice(price?.total)}</b>
            </Trans>
          </Typography>
        </>
      );
      nextButtonText = t`Confirm booking`;
      break;
  }

  return (
    <BookingStepLayout
      nextDisabled={submitDisabled}
      nextAction={onSubmit}
      nextLoading={loading}
      nextButtonText={nextButtonText}
    >
      <Block flushOnMobile>
        <Typography sx={{ mb: 2 }} variant="h3">
          <Trans>Payment</Trans>
        </Typography>
        <Stack spacing={2}>
          {paymentInfo}
          <Divider />
          <LegalConsent value={consent} onChange={setConsent} />
          {isDraftBookingEnabled && (
            <>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                <Trans>
                  Prefer to book later? Save your booking as a draft to complete it later. An email
                  address is required.
                </Trans>
              </Typography>
              <Button
                variant="outlined"
                onClick={saveDraft}
                loading={isSaving}
                disabled={!canSaveDraft || isSaving}
              >
                <Trans>Save booking now</Trans>
              </Button>
            </>
          )}
        </Stack>
      </Block>
      <DraftBookingDialog
        open={!!draftBookingLink}
        draftBookingLink={draftBookingLink ?? ""}
        onClose={clearDraftLink}
      />
    </BookingStepLayout>
  );
}

interface LegalConsentProps {
  value: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
  };
  onChange: (value: LegalConsentProps["value"]) => void;
}

function LegalConsent({ value, onChange }: LegalConsentProps) {
  const { data: campgroundData } = useCampground();

  const tocUrl = campgroundData.campground.toc_url || campgroundData.urls.toc || "#";

  const privacyUrl =
    campgroundData.campground.privacy_policy_url || campgroundData.urls.privacy_policy || "#";

  return (
    <Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={value.termsAccepted}
            onChange={(e) => onChange({ ...value, termsAccepted: e.target.checked })}
          />
        }
        label={
          <Typography variant="body2">
            <Trans>
              I have read the{" "}
              <MuiLink href={tocUrl} target="_blank" rel="noopener noreferrer" fontWeight={600}>
                <Trans>Terms and Conditions</Trans>
              </MuiLink>{" "}
              and agree to them.
            </Trans>
          </Typography>
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={value.privacyAccepted}
            onChange={(e) => onChange({ ...value, privacyAccepted: e.target.checked })}
          />
        }
        label={
          <Typography variant="body2">
            <Trans>
              I have read the{" "}
              <MuiLink href={privacyUrl} target="_blank" rel="noopener noreferrer" fontWeight={600}>
                <Trans>Privacy Policy</Trans>
              </MuiLink>{" "}
              and agree to it.
            </Trans>
          </Typography>
        }
      />
    </Stack>
  );
}
